/**
 * Crop viewport geometry.
 *
 * Single source of truth is the crop `area` (rect in natural media pixels, in the
 * rotated bounding box space) — the same value persisted in DB and consumed by
 * `getCroppedImg`. The on-screen view (`cropSize`, `zoom`, `crop`) is always derived
 * from it, so resizing the canvas can never change the photo/frame framing.
 *
 * `zoom` in react-easy-crop is relative to `mediaSize`, the size at which the media is
 * rendered inside the container ("contain" fit). That size changes on every container
 * resize, which is why zoom must be recomputed instead of kept.
 *
 * All functions here are pure and mirror react-easy-crop's own math
 * (`computeCroppedArea` / `getInitialCropFromCroppedAreaPixels`) with
 * `restrictPosition: false` — the media is allowed to extend past the canvas edges.
 */

import { computeInsetCropSize } from './entityImageCropLayout';

/** Wide bounds passed to the Cropper: real limits are enforced on coverage. */
export const CROP_VIEW_MIN_ZOOM = 0.001;
export const CROP_VIEW_MAX_ZOOM = 1000;

/** Share of the photo captured by the frame — layout independent zoom unit. */
export const CROP_COVERAGE_MIN = 0.05;
/** 10 = 0.1× on the zoom slider (midpoint 1×, right end 10×). */
export const CROP_COVERAGE_MAX = 10;

function isPositive(value) {
  return Number.isFinite(value) && value > 0;
}

export function clampCoverage(coverage) {
  if (!Number.isFinite(coverage)) {
    return 1;
  }
  return Math.min(CROP_COVERAGE_MAX, Math.max(CROP_COVERAGE_MIN, coverage));
}

/** Bounding box of a rectangle rotated by `rotation` degrees. */
export function rotateSize(width, height, rotation = 0) {
  const rad = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(rad) * width) + Math.abs(Math.sin(rad) * height),
    height: Math.abs(Math.sin(rad) * width) + Math.abs(Math.cos(rad) * height),
  };
}

/**
 * Rendered media size, matching the `.reactEasyCrop_Contain` CSS
 * (`max-width: 100%; max-height: 100%`) — fit inside the container, never enlarged.
 *
 * @param {{ width: number, height: number }} natural natural media size
 * @param {{ width: number, height: number }} container
 * @returns {{ width, height, naturalWidth, naturalHeight } | null}
 */
export function computeMediaSize(natural, container) {
  if (!isPositive(natural?.width) || !isPositive(natural?.height)) {
    return null;
  }
  if (!isPositive(container?.width) || !isPositive(container?.height)) {
    return null;
  }

  const scale = Math.min(
    container.width / natural.width,
    container.height / natural.height,
    1,
  );

  return {
    width: natural.width * scale,
    height: natural.height * scale,
    naturalWidth: natural.width,
    naturalHeight: natural.height,
  };
}

/**
 * On-screen crop frame: largest rect of `aspect` keeping a margin to the canvas edges.
 * Always recomputed from the container, so the frame follows canvas growth/shrink.
 */
export function computeFrameSize(container, aspect) {
  return computeInsetCropSize(container?.width, container?.height, aspect);
}

/** Uniform ratio between rendered and natural media size. */
function getMediaZoom(mediaSize) {
  return mediaSize.width > mediaSize.height
    ? mediaSize.width / mediaSize.naturalWidth
    : mediaSize.height / mediaSize.naturalHeight;
}

/**
 * Derive the view (`zoom` + media translation) that puts `area` exactly under the frame.
 * Inverse of {@link areaFromView}.
 *
 * @param {{ area, mediaSize, cropSize, rotation }} params
 * @returns {{ crop: { x: number, y: number }, zoom: number } | null}
 */
export function viewFromArea({ area, mediaSize, cropSize, rotation = 0 }) {
  if (!isPositive(area?.width) || !isPositive(area?.height)) {
    return null;
  }
  if (!mediaSize || !isPositive(cropSize?.width) || !isPositive(cropSize?.height)) {
    return null;
  }

  const naturalBBox = rotateSize(mediaSize.naturalWidth, mediaSize.naturalHeight, rotation);
  const mediaZoom = getMediaZoom(mediaSize);

  // Match react-easy-crop: drive the zoom from the frame's longer axis.
  const zoom =
    cropSize.height > cropSize.width
      ? cropSize.height / (area.height * mediaZoom)
      : cropSize.width / (area.width * mediaZoom);

  const cropZoom =
    cropSize.height > cropSize.width
      ? cropSize.height / area.height
      : cropSize.width / area.width;

  return {
    zoom: Math.min(CROP_VIEW_MAX_ZOOM, Math.max(CROP_VIEW_MIN_ZOOM, zoom)),
    crop: {
      x: ((naturalBBox.width - area.width) / 2 - area.x) * cropZoom,
      y: ((naturalBBox.height - area.height) / 2 - area.y) * cropZoom,
    },
  };
}

/**
 * Compute the crop area captured by the frame for a given view.
 * Mirror of react-easy-crop's `computeCroppedArea` with `restrictPosition: false`.
 *
 * @param {{ crop, zoom, mediaSize, cropSize, rotation }} params
 * @returns {{ x: number, y: number, width: number, height: number } | null}
 */
export function areaFromView({ crop, zoom, mediaSize, cropSize, rotation = 0 }) {
  if (!crop || !isPositive(zoom) || !mediaSize) {
    return null;
  }
  if (!isPositive(cropSize?.width) || !isPositive(cropSize?.height)) {
    return null;
  }

  const mediaBBox = rotateSize(mediaSize.width, mediaSize.height, rotation);
  const naturalBBox = rotateSize(mediaSize.naturalWidth, mediaSize.naturalHeight, rotation);
  if (!isPositive(mediaBBox.width) || !isPositive(mediaBBox.height)) {
    return null;
  }

  const aspect = cropSize.width / cropSize.height;
  const percentages = {
    x: (((mediaBBox.width - cropSize.width / zoom) / 2 - crop.x / zoom) / mediaBBox.width) * 100,
    y: (((mediaBBox.height - cropSize.height / zoom) / 2 - crop.y / zoom) / mediaBBox.height) * 100,
    width: ((cropSize.width / mediaBBox.width) * 100) / zoom,
    height: ((cropSize.height / mediaBBox.height) * 100) / zoom,
  };

  const widthInPixels = Math.round((percentages.width * naturalBBox.width) / 100);
  const heightInPixels = Math.round((percentages.height * naturalBBox.height) / 100);
  // Enforce the exact aspect from the axis with the most pixels to avoid rounding drift.
  const isMediaWiderThanHigh = naturalBBox.width >= naturalBBox.height * aspect;
  const size = isMediaWiderThanHigh
    ? { width: Math.round(heightInPixels * aspect), height: heightInPixels }
    : { width: widthInPixels, height: Math.round(widthInPixels / aspect) };

  return {
    ...size,
    x: Math.round((percentages.x * naturalBBox.width) / 100),
    y: Math.round((percentages.y * naturalBBox.height) / 100),
  };
}

/**
 * Share of the photo width captured by the frame. Independent of canvas size, so it is
 * the stable unit for the zoom slider (small coverage = zoomed in).
 */
export function coverageFromArea(area, natural, rotation = 0) {
  if (!isPositive(area?.width) || !isPositive(natural?.width) || !isPositive(natural?.height)) {
    return null;
  }
  const naturalBBox = rotateSize(natural.width, natural.height, rotation);
  return area.width / naturalBBox.width;
}

/**
 * Scale the area around its own center — which is the media point under the frame
 * center, so this is exactly a zoom pivoted on the crop frame.
 */
export function scaleAreaAroundCenter(area, factor, aspect) {
  if (!isPositive(area?.width) || !isPositive(area?.height) || !isPositive(factor)) {
    return area ?? null;
  }
  const centerX = area.x + area.width / 2;
  const centerY = area.y + area.height / 2;
  const width = area.width * factor;
  const height = isPositive(aspect) ? width / aspect : area.height * factor;

  return {
    x: Math.round(centerX - width / 2),
    y: Math.round(centerY - height / 2),
    width: Math.round(width),
    height: Math.round(height),
  };
}

/** Area of the requested coverage, keeping the current center and frame aspect. */
export function areaFromCoverage(area, coverage, natural, aspect, rotation = 0) {
  const current = coverageFromArea(area, natural, rotation);
  if (!isPositive(current)) {
    return area ?? null;
  }
  return scaleAreaAroundCenter(area, clampCoverage(coverage) / current, aspect);
}

/**
 * Default area when no crop is known yet: the largest centered rect of `aspect`
 * fitting the photo.
 */
export function defaultArea(natural, aspect, rotation = 0) {
  if (!isPositive(natural?.width) || !isPositive(natural?.height) || !isPositive(aspect)) {
    return null;
  }
  const naturalBBox = rotateSize(natural.width, natural.height, rotation);

  let width = naturalBBox.width;
  let height = width / aspect;
  if (height > naturalBBox.height) {
    height = naturalBBox.height;
    width = height * aspect;
  }

  return {
    x: Math.round((naturalBBox.width - width) / 2),
    y: Math.round((naturalBBox.height - height) / 2),
    width: Math.round(width),
    height: Math.round(height),
  };
}
